import React from 'react'
import { motion } from 'framer-motion'
import { Eye, Heart, Users, TrendingUp, Shield, BarChart3, CheckCircle } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'

const Features: React.FC = () => {
  const { t } = useLanguage()

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  }

  return (
    <section id="features" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={containerVariants}
          className="text-center mb-16"
        >
          <motion.h2
            variants={itemVariants}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            {t('features.title')}
          </motion.h2>
          <motion.p
            variants={itemVariants}
            className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed"
          >
            {t('features.description')}
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={containerVariants}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12"
        >
          {/* Transparency Section */}
          <motion.div variants={itemVariants} className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mr-4">
                <Eye className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{t('features.transparency.title')}</h3>
            </div>
            <ul className="space-y-4">
              <motion.li
                variants={itemVariants}
                className="flex items-start space-x-3"
              >
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{t('features.transparency.items.0')}</span>
              </motion.li>
              <motion.li
                variants={itemVariants}
                className="flex items-start space-x-3"
              >
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{t('features.transparency.items.1')}</span>
              </motion.li>
            </ul>
          </motion.div>

          {/* Customer Satisfaction Section */}
          <motion.div variants={itemVariants} className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center mr-4">
                <Heart className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{t('features.satisfaction.title')}</h3>
            </div>
            <ul className="space-y-4">
              <motion.li
                variants={itemVariants}
                className="flex items-start space-x-3"
              >
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{t('features.satisfaction.items.0')}</span>
              </motion.li>
              <motion.li
                variants={itemVariants}
                className="flex items-start space-x-3"
              >
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{t('features.satisfaction.items.1')}</span>
              </motion.li>
            </ul>
          </motion.div>

          {/* Collaboration Section */}
          <motion.div variants={itemVariants} className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center mr-4">
                <Users className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{t('features.collaboration.title')}</h3>
            </div>
            <ul className="space-y-4">
              <motion.li
                variants={itemVariants}
                className="flex items-start space-x-3"
              >
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{t('features.collaboration.items.0')}</span>
              </motion.li>
              <motion.li
                variants={itemVariants}
                className="flex items-start space-x-3"
              >
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{t('features.collaboration.items.1')}</span>
              </motion.li>
            </ul>
          </motion.div>

          {/* Additional Benefits Section */}
          <motion.div variants={itemVariants} className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center mr-4">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{t('features.benefits.title')}</h3>
            </div>
            <ul className="space-y-4">
              <motion.li
                variants={itemVariants}
                className="flex items-start space-x-3"
              >
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{t('features.benefits.items.0')}</span>
              </motion.li>
              <motion.li
                variants={itemVariants}
                className="flex items-start space-x-3"
              >
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{t('features.benefits.items.1')}</span>
              </motion.li>
              <motion.li
                variants={itemVariants}
                className="flex items-start space-x-3"
              >
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{t('features.benefits.items.2')}</span>
              </motion.li>
            </ul>
          </motion.div>
        </motion.div>

        {/* Target Users and Key Features */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={containerVariants}
          className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-12"
        >
          {/* Target Users */}
          <motion.div variants={itemVariants} className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-indigo-500 rounded-lg flex items-center justify-center mr-4">
                <Users className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{t('features.targetUsers.title')}</h3>
            </div>
            <ul className="space-y-4">
              <motion.li
                variants={itemVariants}
                className="flex items-start space-x-3"
              >
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{t('features.targetUsers.items.0')}</span>
              </motion.li>
              <motion.li
                variants={itemVariants}
                className="flex items-start space-x-3"
              >
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{t('features.targetUsers.items.1')}</span>
              </motion.li>
              <motion.li
                variants={itemVariants}
                className="flex items-start space-x-3"
              >
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{t('features.targetUsers.items.2')}</span>
              </motion.li>
            </ul>
          </motion.div>

          {/* Key Features */}
          <motion.div variants={itemVariants} className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center mr-4">
                <BarChart3 className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{t('features.keyFeatures.title')}</h3>
            </div>
            <ul className="space-y-4">
              <motion.li
                variants={itemVariants}
                className="flex items-start space-x-3"
              >
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{t('features.keyFeatures.items.0')}</span>
              </motion.li>
              <motion.li
                variants={itemVariants}
                className="flex items-start space-x-3"
              >
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{t('features.keyFeatures.items.1')}</span>
              </motion.li>
              <motion.li
                variants={itemVariants}
                className="flex items-start space-x-3"
              >
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{t('features.keyFeatures.items.2')}</span>
              </motion.li>
              <motion.li
                variants={itemVariants}
                className="flex items-start space-x-3"
              >
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{t('features.keyFeatures.items.3')}</span>
              </motion.li>
            </ul>
          </motion.div>
        </motion.div>

        {/* Conclusion */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
            <Shield className="h-12 w-12 mx-auto mb-4" />
            <p className="text-xl leading-relaxed max-w-4xl mx-auto">
              <strong>{t('features.conclusion')}</strong>
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default Features
